/**
 * Checks if a direct straight line exists between two points.
 */
export const isDirectPath = (p1, p2, grid) => {
  if (p1.x !== p2.x && p1.y !== p2.y) return false;

  const minX = Math.min(p1.x, p2.x);
  const maxX = Math.max(p1.x, p2.x);
  const minY = Math.min(p1.y, p2.y);
  const maxY = Math.max(p1.y, p2.y);

  if (p1.x === p2.x) {
    for (let y = minY + 1; y < maxY; y++) {
      if (grid[y][p1.x].isVisible) return false;
    }
  } else {
    for (let x = minX + 1; x < maxX; x++) {
      if (grid[p1.y][x].isVisible) return false;
    }
  }
  return true;
};

/**
 * Checks path with at most 2 turns.
 */
export const findPath = (p1, p2, grid) => {
  const rows = grid.length;
  const cols = grid[0].length;

  // 0 Turn (Direct)
  if (isDirectPath(p1, p2, grid)) {
    return [p1, p2];
  }

  // 1 Turn (L-shape)
  // Corner 1: (p1.x, p2.y)
  const c1 = { x: p1.x, y: p2.y };
  if (!grid[c1.y][c1.x].isVisible && isDirectPath(p1, c1, grid) && isDirectPath(c1, p2, grid)) {
    return [p1, c1, p2];
  }
  // Corner 2: (p2.x, p1.y)
  const c2 = { x: p2.x, y: p1.y };
  if (!grid[c2.y][c2.x].isVisible && isDirectPath(p1, c2, grid) && isDirectPath(c2, p2, grid)) {
    return [p1, c2, p2];
  }

  // 2 Turns (Z-shape or U-shape)
  // Horizontal scan
  for (let x = 0; x < cols; x++) {
    const tempP1 = { x, y: p1.y };
    const tempP2 = { x, y: p2.y };

    // Check if the vertical bridge exists
    if (x !== p1.x && grid[tempP1.y][tempP1.x].isVisible) continue;
    if (x !== p2.x && grid[tempP2.y][tempP2.x].isVisible) continue;

    if (isDirectPath(p1, tempP1, grid) && isDirectPath(tempP1, tempP2, grid) && isDirectPath(tempP2, p2, grid)) {
      return [p1, tempP1, tempP2, p2];
    }
  }

  // Vertical scan
  for (let y = 0; y < rows; y++) {
    const tempP1 = { x: p1.x, y };
    const tempP2 = { x: p2.x, y };

    if (y !== p1.y && grid[tempP1.y][tempP1.x].isVisible) continue;
    if (y !== p2.y && grid[tempP2.y][tempP2.x].isVisible) continue;

    if (isDirectPath(p1, tempP1, grid) && isDirectPath(tempP1, tempP2, grid) && isDirectPath(tempP2, p2, grid)) {
      return [p1, tempP1, tempP2, p2];
    }
  }

  return null;
};

/**
 * Checks if any valid moves remain in the grid.
 */
export const checkAnyMoves = (grid) => {
  const flattened = grid.flat().filter(t => t.isVisible);
  for (let i = 0; i < flattened.length; i++) {
    for (let j = i + 1; j < flattened.length; j++) {
      const t1 = flattened[i];
      const t2 = flattened[j];
      if (t1.type === t2.type) {
        if (findPath(t1.pos, t2.pos, grid)) {
          return { p1: t1.pos, p2: t2.pos };
        }
      }
    }
  }
  return null;
};

/**
 * Shuffles current visible tiles.
 */
export const shuffleGrid = (grid) => {
  const visibleTiles = grid.flat().filter(t => t.isVisible);
  const types = visibleTiles.map(t => t.type);
  
  // Fisher-Yates shuffle
  for (let i = types.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [types[i], types[j]] = [types[j], types[i]];
  }

  let typeIdx = 0;
  return grid.map(row => row.map(tile => {
    if (tile.isVisible) {
      return { ...tile, type: types[typeIdx++] };
    }
    return tile;
  }));
};